import { useEffect, useState } from "react";
import { Clock, MapPin, Phone } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { SITE, zurichNow } from "@/data/site";
import { InstagramIcon, Spark } from "./Icons";
import { Marquee } from "./Reveal";

export const useOpenNow = () => {
  const [state, setState] = useState(zurichNow);
  useEffect(() => {
    const id = setInterval(() => setState(zurichNow()), 60000);
    return () => clearInterval(id);
  }, []);
  return state;
};

export const InfoStrip = () => {
  const { t } = useLang();
  const { open } = useOpenNow();
  const cells = [
    {
      id: "hours",
      icon: <Clock className="w-5 h-5" />,
      label: t.strip.hoursLabel,
      value: t.strip.hours,
      extra: (
        <span data-testid="open-now-indicator" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest mt-1">
          <span className={`w-2 h-2 rounded-full ${open ? "bg-green-600 animate-pulse" : "bg-hammer-red"}`} />
          {open ? t.strip.openNow : t.strip.closedNow}
        </span>
      ),
    },
    { id: "address", icon: <MapPin className="w-5 h-5" />, label: t.strip.addressLabel, value: `${SITE.street}, ${SITE.city}`, href: SITE.mapsSearch },
    { id: "phone", icon: <Phone className="w-5 h-5" />, label: t.strip.callLabel, value: SITE.phoneDisplay, href: SITE.tel },
    { id: "instagram", icon: <InstagramIcon className="w-5 h-5" />, label: t.strip.igLabel, value: SITE.instagramHandle, href: SITE.instagram },
  ];
  return (
    <section id="info" data-testid="info-strip" className="paper border-b-2 border-ink">
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {cells.map((c, i) => {
          const inner = (
            <>
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
                <span className="text-hammer-red">{c.icon}</span> {c.label}
              </span>
              <span className="font-display text-[2rem] leading-none mt-2 text-ink" data-testid={`info-${c.id}-value`}>
                {c.value}
              </span>
              {c.extra}
            </>
          );
          const cls = `flex flex-col px-5 sm:px-8 py-6 border-ink/80 ${i > 0 ? "border-t-2 sm:border-t-0" : ""} ${
            i % 2 === 1 ? "sm:border-l-2" : ""
          } ${i >= 2 ? "sm:border-t-2 lg:border-t-0" : ""} ${i > 0 ? "lg:border-l-2" : ""}`;
          return c.href ? (
            <a
              key={c.id}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid={`info-${c.id}-link`}
              className={`${cls} hover:bg-ink hover:[&_*]:text-bone transition-colors duration-200`}
            >
              {inner}
            </a>
          ) : (
            <div key={c.id} className={cls}>
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const MottoBand = () => {
  const { t } = useLang();
  return (
    <section aria-label="motto" data-testid="motto-band" className="relative bg-bone overflow-hidden py-14 sm:py-20">
      <div className="absolute inset-x-[-5%] top-1/2 -translate-y-1/2 rotate-[3deg] bg-ink py-3">
        <Marquee
          items={["HAMMER GYM", "VIA FOLA 13", "06–22", "7/7", "HAMMER STRENGTH"]}
          reverse
          speed={40}
          separator={<span className="mx-6 text-hammer-red">✕</span>}
          className="font-display text-3xl sm:text-4xl text-outline"
        />
      </div>
      <div className="relative -rotate-[2.5deg] bg-hammer-red border-y-4 border-ink py-3 sm:py-4 -mx-[5%] shadow-[0_10px_0_rgba(15,15,16,0.9)]">
        <Marquee
          items={t.band}
          speed={32}
          separator={<Spark className="mx-5 sm:mx-8 w-6 h-6 sm:w-8 sm:h-8 text-ink" />}
          className="font-display text-5xl sm:text-6xl lg:text-7xl text-bone leading-none pt-2"
        />
      </div>
    </section>
  );
};
